import React from "react";
import styled from "@emotion/styled";
import { ApplicationRevenue, OrganizationRevenue } from "../../../models/revenue/BillingConfig";
import {
    color_text_default,
    color_text_light,
    color_colors_ocean,
    color_colors_growth,
    color_colors_decline,
    color_defaults_white,
    color_shades_dark,
    color_shades_light,
    color_shades_lightest,
    color_shades_darker,
} from "../../../constants/colors";

interface RevenueTableProps {
    orgRevenue: OrganizationRevenue;
    ignoredAppIds: string[];
    onToggleIgnore: (appId: string) => void;
    onEditOverride: (appId: string) => void;
    hasOverride: (appId: string) => boolean;
}

const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

const formatNumber = (value: number, decimals: number = 1): string => {
    return value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
    });
};

const RevenueTable: React.FC<RevenueTableProps> = ({
    orgRevenue,
    ignoredAppIds,
    onToggleIgnore,
    onEditOverride,
    hasOverride,
}) => {
    if (orgRevenue.applications.length === 0) {
        return (
            <EmptyState>
                No billable applications found for this month. 
                {ignoredAppIds.length > 0 && ` (${ignoredAppIds.length} applications are currently ignored)`}
            </EmptyState>
        );
    }

    return (
        <TableWrapper>
            <Table>
                <thead>
                    <tr>
                        <Th>Application</Th>
                        <ThOrders align="right">Orders</ThOrders>
                        <Th align="right">Inbound AI Min</Th>
                        <Th align="right">Outbound AI Min</Th>
                        <Th align="right">PT Min</Th>
                        <Th align="right">Calls</Th>
                        <Th align="right">Contained</Th>
                        <Th align="right">Non-Containable</Th>
                        <Th align="right">Conversations</Th>
                        <Th align="right">Flat Fee</Th>
                        <Th align="right">Total Revenue</Th>
                        <Th align="center">Actions</Th>
                    </tr>
                </thead>
                <tbody>
                    {orgRevenue.applications.map((app) => {
                        const isIgnored = ignoredAppIds.includes(app.applicationId);
                        const isOverridden = hasOverride(app.applicationId);

                        return (
                            <Tr key={app.applicationId} isIgnored={isIgnored}>
                                <TdName>
                                    <AppNameCell>
                                        <AppName>{app.applicationName}</AppName>
                                        {isOverridden && <OverrideBadge>Override</OverrideBadge>}
                                    </AppNameCell>
                                </TdName>
                                <TdOrders align="right">
                                    <MetricCell>
                                        <MetricCountBold>{formatNumber(app.breakdown.ordersPlaced.count, 0)}</MetricCountBold>
                                        <MetricRevenueBold>{formatCurrency(app.breakdown.ordersPlaced.revenue)}</MetricRevenueBold>
                                    </MetricCell>
                                </TdOrders>
                                <Td align="right">
                                    <MetricCell>
                                        <MetricCount>{formatNumber(app.breakdown.aiMinutes.count)}</MetricCount>
                                        <MetricRevenue>{formatCurrency(app.breakdown.aiMinutes.revenue)}</MetricRevenue>
                                    </MetricCell>
                                </Td>
                                <Td align="right">
                                    <MetricCell>
                                        <MetricCount>{formatNumber(app.breakdown.outboundAiMinutes.count)}</MetricCount>
                                        <MetricRevenue>{formatCurrency(app.breakdown.outboundAiMinutes.revenue)}</MetricRevenue>
                                    </MetricCell>
                                </Td>
                                <Td align="right">
                                    <MetricCell>
                                        <MetricCount>{formatNumber(app.breakdown.postTransferMinutes.count)}</MetricCount>
                                        <MetricRevenue>{formatCurrency(app.breakdown.postTransferMinutes.revenue)}</MetricRevenue>
                                    </MetricCell>
                                </Td>
                                <Td align="right">
                                    <MetricCell>
                                        <MetricCount>{formatNumber(app.breakdown.calls.count, 0)}</MetricCount>
                                        <MetricRevenue>{formatCurrency(app.breakdown.calls.revenue)}</MetricRevenue>
                                    </MetricCell>
                                </Td>
                                <Td align="right">
                                    <MetricCell>
                                        <MetricCount>{formatNumber(app.breakdown.containedCalls.count, 0)}</MetricCount>
                                        <MetricRevenue>{formatCurrency(app.breakdown.containedCalls.revenue)}</MetricRevenue>
                                    </MetricCell>
                                </Td>
                                <Td align="right">
                                    <MetricCell>
                                        <MetricCount>{formatNumber(app.breakdown.nonContainableCalls.count, 0)}</MetricCount>
                                        <MetricRevenue>{formatCurrency(app.breakdown.nonContainableCalls.revenue)}</MetricRevenue>
                                    </MetricCell>
                                </Td>
                                <Td align="right">{formatNumber(app.engagementCount, 0)}</Td>
                                <Td align="right">{formatCurrency(app.breakdown.flatFee)}</Td>
                                <TdTotal align="right">{formatCurrency(app.totalRevenue)}</TdTotal>
                                <Td align="center">
                                    <ActionGroup>
                                        <SmallButton
                                            onClick={() => onEditOverride(app.applicationId)}
                                            title={isOverridden ? "Edit billing override" : "Add billing override"}
                                        >
                                            {isOverridden ? "Edit" : "Override"}
                                        </SmallButton>
                                        <IgnoreButton
                                            isIgnored={isIgnored}
                                            onClick={() => onToggleIgnore(app.applicationId)}
                                            title={isIgnored ? "Include this application" : "Ignore this application"}
                                        >
                                            {isIgnored ? "Include" : "Ignore"}
                                        </IgnoreButton>
                                    </ActionGroup>
                                </Td>
                            </Tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <TotalRow>
                        <Td><strong>Organization Total</strong></Td>
                        <TdOrders align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.ordersPlaced.count, 0), 0)}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.ordersPlaced.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </TdOrders>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.aiMinutes.count, 0))}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.aiMinutes.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.outboundAiMinutes.count, 0))}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.outboundAiMinutes.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.postTransferMinutes.count, 0))}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.postTransferMinutes.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.calls.count, 0), 0)}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.calls.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.containedCalls.count, 0), 0)}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.containedCalls.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.breakdown.nonContainableCalls.count, 0), 0)}
                                </MetricCountBold>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.nonContainableCalls.revenue, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricCountBold>
                                    {formatNumber(orgRevenue.applications.reduce((s, a) => s + a.engagementCount, 0), 0)}
                                </MetricCountBold>
                            </MetricCell>
                        </Td>
                        <Td align="right">
                            <MetricCell>
                                <MetricRevenueBold>
                                    {formatCurrency(orgRevenue.applications.reduce((s, a) => s + a.breakdown.flatFee, 0))}
                                </MetricRevenueBold>
                            </MetricCell>
                        </Td>
                        <TdTotal align="right">
                            <strong>{formatCurrency(orgRevenue.totalRevenue)}</strong>
                        </TdTotal>
                        <Td />
                    </TotalRow>
                </tfoot>
            </Table>
        </TableWrapper>
    );
};

const TableWrapper = styled.div`
    overflow-x: auto;
    border: 1px solid ${color_shades_light};
    border-radius: 6px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    thead tr th:nth-child(even),
    tbody tr td:nth-child(even),
    tfoot tr td:nth-child(even) {
        background-color: rgba(0, 0, 0, 0.025);
    }
`;

const Th = styled.th<{ align?: string }>`
    padding: 10px 8px;
    text-align: ${(p) => p.align || "left"};
    font-size: 11px;
    font-weight: 600;
    color: ${color_text_light};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: ${color_shades_lightest};
    border-bottom: 2px solid ${color_shades_dark};
    white-space: nowrap;
`;

const Tr = styled.tr<{ isIgnored?: boolean }>`
    opacity: ${(p) => (p.isIgnored ? 0.4 : 1)};
    text-decoration: ${(p) => (p.isIgnored ? "line-through" : "none")};

    &:hover {
        background: ${color_shades_lightest};
    }

    &:not(:last-child) td {
        border-bottom: 1px solid ${color_shades_light};
    }
`;

const Td = styled.td<{ align?: string }>`
    padding: 8px;
    text-align: ${(p) => p.align || "left"};
    color: ${color_text_default};
    white-space: nowrap;
`;

const TdName = styled.td`
    padding: 8px;
    text-align: left;
    color: ${color_text_default};
`;

const TdTotal = styled(Td)`
    font-weight: 600;
    color: ${color_colors_growth};
`;

const TotalRow = styled.tr`
    background: ${color_shades_lightest};

    td {
        border-top: 2px solid ${color_shades_dark};
        padding: 10px 8px;
    }
`;

const AppNameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const AppName = styled.span`
    font-weight: 500;
`;

const OverrideBadge = styled.span`
    font-size: 10px;
    padding: 1px 5px;
    border-radius: 3px;
    background: ${color_colors_ocean};
    color: ${color_defaults_white};
    font-weight: 500;
`;

const MetricCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
`;

const MetricCount = styled.span`
    font-size: 12px;
    color: ${color_text_light};
`;

const MetricRevenue = styled.span`
    font-size: 13px;
    font-weight: 500;
`;

const ActionGroup = styled.div`
    display: flex;
    gap: 4px;
    justify-content: center;
`;

const SmallButton = styled.button`
    padding: 3px 8px;
    font-size: 11px;
    border: 1px solid ${color_colors_ocean};
    color: ${color_colors_ocean};
    background: transparent;
    border-radius: 3px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${color_colors_ocean};
        color: ${color_defaults_white};
    }
`;

const IgnoreButton = styled.button<{ isIgnored?: boolean }>`
    padding: 3px 8px;
    font-size: 11px;
    border: 1px solid ${(p) => (p.isIgnored ? color_colors_growth : color_colors_decline)};
    color: ${(p) => (p.isIgnored ? color_colors_growth : color_colors_decline)};
    background: transparent;
    border-radius: 3px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${(p) => (p.isIgnored ? color_colors_growth : color_colors_decline)};
        color: ${color_defaults_white};
    }
`;

const EmptyState = styled.div`
    padding: 24px;
    text-align: center;
    color: ${color_text_light};
    font-style: italic;
`;

const ThOrders = styled(Th)`
    color: ${color_text_default};
    font-size: 12px;
`;

const TdOrders = styled(Td)`
    font-weight: 600;
`;

const MetricCountBold = styled(MetricCount)`
    font-weight: 600;
    color: ${color_text_default};
`;

const MetricRevenueBold = styled(MetricRevenue)`
    font-weight: 700;
`;

export default React.memo(RevenueTable);
